import React, { useState, useEffect } from 'react';
import './SavedReports.css';

function SavedReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewModal, setPreviewModal] = useState(null);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/datatrace/saved-reports');
      const data = await response.json();
      setReports(data.reports || []);
    } catch (error) {
      console.error('Failed to load saved reports:', error);
      alert('Failed to load saved reports: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/datatrace/saved-reports/${encodeURIComponent(filename)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      // Reload the list
      loadReports();
    } catch (error) {
      alert('Failed to delete file: ' + error.message);
    }
  };

  const extractDocumentLinks = (obj, parentKey = '') => {
    const links = [];

    if (typeof obj !== 'object' || obj === null) return links;

    // Check if this is an array of document objects
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        const itemKey = parentKey ? `${parentKey}.${index}` : String(index);

        // If this object has both uri/url and name fields, extract them together
        if (typeof item === 'object' && item !== null) {
          let url = null;
          let name = null;

          // Look for URL field
          for (const key in item) {
            const value = item[key];
            if (typeof value === 'string' &&
                (key.toLowerCase().includes('url') ||
                 key.toLowerCase().includes('uri') ||
                 key.toLowerCase().includes('link')) &&
                (value.startsWith('http://') || value.startsWith('https://'))) {
              url = value;
            }
            if (key.toLowerCase() === 'name' && typeof value === 'string') {
              name = value;
            }
          }

          if (url) {
            links.push({
              key: itemKey,
              url: url,
              name: name || itemKey
            });
          } else {
            // Recursively search this object
            links.push(...extractDocumentLinks(item, itemKey));
          }
        }
      });
    } else {
      // Regular object traversal
      for (const key in obj) {
        const value = obj[key];
        const fullKey = parentKey ? `${parentKey}.${key}` : key;

        // Look for document URLs
        if (typeof value === 'string' &&
            (key.toLowerCase().includes('url') ||
             key.toLowerCase().includes('uri') ||
             key.toLowerCase().includes('link') ||
             key.toLowerCase().includes('document') ||
             key.toLowerCase().includes('pdf')) &&
            (value.startsWith('http://') || value.startsWith('https://'))) {
          links.push({ key: fullKey, url: value, name: fullKey });
        }

        // Recursively search nested objects and arrays
        if (typeof value === 'object') {
          links.push(...extractDocumentLinks(value, fullKey));
        }
      }
    }

    return links;
  };

  const handlePreview = async (report) => {
    // Only preview JSON files
    if (!report.filename.endsWith('.json')) {
      window.open(report.url, '_blank');
      return;
    }

    try {
      // Fetch the JSON report
      const response = await fetch(report.url);
      const data = await response.json();

      // Fetch the locally saved documents for this report
      const docsResponse = await fetch(`/api/datatrace/saved-reports/${encodeURIComponent(report.filename)}/documents`);
      const docsData = await docsResponse.json();

      setPreviewModal({
        filename: report.filename,
        content: JSON.stringify(data, null, 2),
        localDocuments: docsData.documents || []
      });
    } catch (error) {
      alert('Failed to preview file: ' + error.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileType = (filename) => {
    if (filename.endsWith('_report.json')) return 'Report';
    if (filename.endsWith('.pdf')) return 'PDF';
    if (filename.endsWith('.xml')) return 'XML';
    if (filename.endsWith('.json')) return 'JSON';
    if (filename.endsWith('.html')) return 'HTML';
    return 'File';
  };

  const filteredReports = reports.filter(report =>
    report.filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="saved-reports">
      <div className="saved-reports-header">
        <div>
          <h2>Saved Reports</h2>
          <p className="saved-reports-subtitle">
            View and download all saved DataTrace reports and documents
          </p>
        </div>
        <button className="refresh-btn" onClick={loadReports} disabled={loading}>
          {loading ? '⏳ Loading...' : '🔄 Refresh'}
        </button>
      </div>

      <div className="saved-reports-controls">
        <input
          type="text"
          placeholder="Search by filename..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        <div className="saved-reports-stats">
          <strong>Total Files:</strong> {reports.length}
          {searchQuery && <span> (Filtered: {filteredReports.length})</span>}
        </div>
      </div>

      {loading && reports.length === 0 ? (
        <div className="loading-message">Loading saved reports...</div>
      ) : reports.length === 0 ? (
        <div className="empty-state">
          <h3>No Saved Reports</h3>
          <p>Use the "Save Report & Docs" button in Transaction History to save reports.</p>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="empty-state">
          <h3>No Matching Files</h3>
          <p>Try adjusting your search query.</p>
        </div>
      ) : (
        <div className="reports-list">
          {filteredReports.map((report) => (
            <div key={report.filename} className="report-item">
              <div className="report-info">
                <div className="report-filename">
                  <span className="file-type-badge">{getFileType(report.filename)}</span>
                  {report.filename}
                </div>
                <div className="report-meta">
                  <span>{formatFileSize(report.size)}</span>
                  <span>•</span>
                  <span>{formatDate(report.modified)}</span>
                </div>
              </div>
              <div className="report-actions">
                <button
                  className="preview-btn"
                  onClick={() => handlePreview(report)}
                  title="Preview or open file"
                >
                  {report.filename.endsWith('.json') ? '👁️ Preview' : '🔗 Open'}
                </button>
                <a
                  href={report.url}
                  download={report.filename}
                  className="download-btn"
                  title="Download file"
                >
                  ⬇️ Download
                </a>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(report.filename)}
                  title="Delete file"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewModal && (
        <div className="preview-modal-overlay" onClick={() => setPreviewModal(null)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>{previewModal.filename}</h3>
              <button className="modal-close-btn" onClick={() => setPreviewModal(null)}>×</button>
            </div>
            <div className="preview-modal-content">
              {/* Local Documents Section */}
              {previewModal.localDocuments && previewModal.localDocuments.length > 0 ? (
                <div className="document-links-section">
                  <h4>📄 Saved Documents ({previewModal.localDocuments.length})</h4>
                  <p className="document-links-subtitle">
                    PDF documents saved locally from this report:
                  </p>
                  <div className="document-links-list">
                    {previewModal.localDocuments.map((doc, index) => (
                      <div key={index} className="document-link-item">
                        <div className="document-link-info">
                          <span className="document-index">#{index + 1}</span>
                          <span className="document-path">{doc.displayName}</span>
                        </div>
                        <div className="document-link-actions">
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="doc-open-btn"
                            title="Open in new tab"
                          >
                            🔗 Open
                          </a>
                          <a
                            href={doc.url}
                            download={doc.displayName}
                            className="doc-download-btn"
                            title="Download PDF"
                          >
                            ⬇️ Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="document-links-section">
                  <h4>📄 Saved Documents</h4>
                  <p className="document-links-subtitle">
                    No documents have been downloaded yet. Use the "Save Report & Docs" button in Transaction History to download documents for this report.
                  </p>
                </div>
              )}

              {/* JSON Content */}
              <div className="json-content-section">
                <h4>📋 JSON Content</h4>
                <pre className="preview-code">{previewModal.content}</pre>
              </div>
            </div>
            <div className="preview-modal-actions">
              <button
                className="copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(previewModal.content);
                  alert('JSON content copied to clipboard!');
                }}
              >
                📋 Copy JSON
              </button>
              {previewModal.localDocuments && previewModal.localDocuments.length > 0 && (
                <button
                  className="copy-links-btn"
                  onClick={() => {
                    const baseUrl = window.location.origin;
                    const linksList = previewModal.localDocuments.map((doc, i) =>
                      `${i + 1}. ${doc.displayName}\n   ${baseUrl}${doc.url}`
                    ).join('\n\n');
                    navigator.clipboard.writeText(linksList);
                    alert('Document URLs copied to clipboard!');
                  }}
                >
                  📎 Copy Links
                </button>
              )}
              <button className="close-btn" onClick={() => setPreviewModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SavedReports;
